import React, { useState, useEffect } from 'react';

interface User {
  id: string;
  email: string | null;
  name: string;
  isAnonymous: boolean;
  permissions: string[];
  mobile_number?: string | null;
  otp_delivery_method?: 'email' | 'sms';
}

interface AccountProfileProps {
  user: User;
  onUserUpdate?: () => void;
}

export const AccountProfile: React.FC<AccountProfileProps> = ({ user, onUserUpdate }) => {
  const [name, setName] = useState(user.name || '');
  const [mobileNumber, setMobileNumber] = useState(user.mobile_number || '');
  const [originalMobileNumber] = useState(user.mobile_number || '');

  // Verification state
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedNumber, setVerifiedNumber] = useState(user.mobile_number || '');

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isRequestingVerification, setIsRequestingVerification] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // OTP delivery preference state
  const [otpDeliveryMethod, setOtpDeliveryMethod] = useState<'email' | 'sms'>(user.otp_delivery_method || 'email');
  const [isSavingOtpPreference, setIsSavingOtpPreference] = useState(false);

  // Format initial mobile number on mount
  useEffect(() => {
    if (user.mobile_number) {
      // Format the user's existing mobile number for display
      const formatted = user.mobile_number.replace(/[^\d+]/g, '');

      // If it's a US/Canada number (starts with +1), format it nicely
      if (formatted.startsWith('+1') && formatted.length >= 12) {
        const digitsOnly = formatted.substring(1);
        const areaCode = digitsOnly.substring(1, 4);
        const firstPart = digitsOnly.substring(4, 7);
        const secondPart = digitsOnly.substring(7, 11);
        setMobileNumber(`+1 (${areaCode}) ${firstPart}-${secondPart}`);
      } else {
        // For other international numbers, just display as-is
        setMobileNumber(formatted);
      }

      setVerifiedNumber(user.mobile_number);
      setIsVerified(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-numeric characters except leading +
    const cleaned = value.replace(/[^\d+]/g, '');

    // Ensure it starts with +
    if (!cleaned.startsWith('+')) {
      return cleaned ? `+${cleaned}` : '';
    }

    // Extract country code and number
    const digitsOnly = cleaned.substring(1);

    // If it's a US/Canada number (country code 1), format it nicely
    if (digitsOnly.startsWith('1') && digitsOnly.length >= 11) {
      const countryCode = '1';
      const areaCode = digitsOnly.substring(1, 4);
      const firstPart = digitsOnly.substring(4, 7);
      const secondPart = digitsOnly.substring(7, 11);
      return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
    }

    // For other international numbers, just keep the + and digits
    return cleaned;
  };

  const normalizePhoneNumber = (formatted: string): string => {
    // Convert to E.164 format (+[country code][number])
    const cleaned = formatted.replace(/[^\d+]/g, '');

    // Ensure it starts with +
    if (!cleaned.startsWith('+')) {
      return cleaned ? `+${cleaned}` : '';
    }

    return cleaned;
  };

  const handleMobileNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setMobileNumber(formatted);
    setError(null);
    setSuccess(null);

    // Reset verification if mobile number changes
    if (verificationSent) {
      setVerificationSent(false);
      setVerificationCode('');
      setIsVerifying(false);
    }

    // Reset verified state if number changes from verified number
    const normalized = normalizePhoneNumber(formatted || '');
    const normalizedVerified = normalizePhoneNumber(verifiedNumber || '');
    if (normalized !== normalizedVerified) {
      setIsVerified(false);
    }
  };

  const handleRequestVerification = async () => {
    const normalized = normalizePhoneNumber(mobileNumber);
    // E.164 format: + followed by 10-15 digits (matches API validation)
    if (!normalized || !normalized.match(/^\+\d{10,15}$/)) {
      setError('Please enter a valid international phone number (e.g., +1234567890)');
      return;
    }

    setIsRequestingVerification(true);
    setError(null);

    try {
      const normalized = normalizePhoneNumber(mobileNumber);
      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/user/profile/request-mobile-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ mobile_number: normalized }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send verification code');
      }

      setVerificationSent(true);
      setIsVerifying(true);
      setResendCooldown(60); // 60 second cooldown
      setSuccess('Verification code sent! Please check your phone.');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setIsRequestingVerification(false);
    }
  };

  const handleVerifyAndSave = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const normalized = normalizePhoneNumber(mobileNumber);
      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/user/profile/verify-and-update-mobile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          mobile_number: normalized,
          verification_code: verificationCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify and update mobile number');
      }

      setSuccess('Mobile number verified and updated successfully!');
      setIsVerifying(false);
      setVerificationSent(false);
      setVerificationCode('');
      setIsVerified(true);
      setVerifiedNumber(normalized);

      // Call onUserUpdate to refresh user data
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify and update mobile number');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveName = async () => {
    if (!name || name.trim() === '') {
      setError('Name cannot be empty');
      return;
    }

    if (name === user.name) {
      setError('No changes to save');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/user/profile/update-name', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: name.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update name');
      }

      setSuccess('Name updated successfully!');

      // Call onUserUpdate to refresh user data
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update name');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveOtpPreference = async () => {
    setIsSavingOtpPreference(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/user/profile/update-otp-preference', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ otp_delivery_method: otpDeliveryMethod }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update verification method');
      }

      setSuccess(data.message || 'Verification method updated successfully!');

      // Call onUserUpdate to refresh user data
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update verification method');
    } finally {
      setIsSavingOtpPreference(false);
    }
  };

  const mobileNumberChanged = normalizePhoneNumber(mobileNumber || '') !== normalizePhoneNumber(originalMobileNumber || '');
  const currentNumberIsVerified = isVerified && normalizePhoneNumber(mobileNumber || '') === normalizePhoneNumber(verifiedNumber || '');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-[#080808] mb-4">Account Profile</h3>
        <p className="text-sm text-[#6B7280] mb-6">
          Manage your account information and contact details.
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Email (read-only) */}
      <div>
        <label className="block text-sm font-medium text-[#080808] mb-2">
          Email Address
        </label>
        <input
          type="email"
          value={user.email || ''}
          disabled
          className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-[#6B7280] cursor-not-allowed"
        />
        <p className="mt-1 text-xs text-[#6B7280]">Email address cannot be changed</p>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-[#080808] mb-2">
          Full Name
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
              setSuccess(null);
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C] focus:border-transparent"
            placeholder="Enter your full name"
          />
          <button
            onClick={handleSaveName}
            disabled={isSaving || name === user.name}
            className="px-4 py-2 bg-[#74AA9C] text-white rounded-lg hover:bg-[#5D8A7E] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Mobile Number */}
      <div>
        <label className="block text-sm font-medium text-[#080808] mb-2">
          Mobile Number
        </label>
        <div className="flex gap-2">
          <input
            type="tel"
            value={mobileNumber}
            onChange={handleMobileNumberChange}
            disabled={isVerifying}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="+1234567890"
          />
          {!isVerifying && mobileNumberChanged && !currentNumberIsVerified && (
            <button
              onClick={handleRequestVerification}
              disabled={isRequestingVerification || !mobileNumber}
              className="px-4 py-2 bg-[#74AA9C] text-white rounded-lg hover:bg-[#5D8A7E] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isRequestingVerification ? 'Sending...' : 'Verify'}
            </button>
          )}
          {currentNumberIsVerified && (
            <div className="flex items-center px-4 py-2 mr-2 bg-green-50 border border-green-200 rounded-lg shrink-0">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium text-green-800">Verified</span>
            </div>
          )}
        </div>
        <p className="mt-1 text-xs text-[#6B7280]">
          {currentNumberIsVerified
            ? 'This mobile number has been verified'
            : mobileNumberChanged && !isVerifying
              ? 'Click Verify to confirm your new mobile number via SMS'
              : 'Enter in international format (e.g., +1234567890). Used for SMS notifications and account security.'}
        </p>
      </div>

      {/* Verification Code Input */}
      {isVerifying && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#080808] mb-2">
              Verification Code
            </label>
            <p className="text-sm text-[#6B7280] mb-3">
              Enter the 6-digit code sent to {mobileNumber}
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                  setError(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#74AA9C] focus:border-transparent text-center text-lg tracking-widest font-mono"
                placeholder="000000"
                maxLength={6}
              />
              <button
                onClick={handleVerifyAndSave}
                disabled={isSaving || verificationCode.length !== 6}
                className="px-6 py-2 bg-[#74AA9C] text-white rounded-lg hover:bg-[#5D8A7E] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Verifying...' : 'Verify & Save'}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <button
              onClick={() => {
                setIsVerifying(false);
                setVerificationSent(false);
                setVerificationCode('');
                setMobileNumber(originalMobileNumber);
              }}
              className="text-[#6B7280] hover:text-[#080808] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRequestVerification}
              disabled={resendCooldown > 0 || isRequestingVerification}
              className="text-[#74AA9C] hover:text-[#5D8A7E] transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {resendCooldown > 0
                ? `Resend code in ${resendCooldown}s`
                : isRequestingVerification
                  ? 'Sending...'
                  : 'Resend code'}
            </button>
          </div>
        </div>
      )}

      {/* Login Verification Method - Only show when mobile is verified */}
      {currentNumberIsVerified && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-medium text-[#080808] mb-3">Login Verification Method</h4>
          <p className="text-sm text-[#6B7280] mb-4">
            Choose how you want to receive verification codes when you sign in.
          </p>

          <div className="space-y-3">
            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="otpDeliveryMethod"
                value="email"
                checked={otpDeliveryMethod === 'email'}
                onChange={() => setOtpDeliveryMethod('email')}
                className="mt-0.5 w-4 h-4 text-[#74AA9C] focus:ring-[#74AA9C] border-gray-300"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-[#080808]">Email</span>
                <p className="text-xs text-[#6B7280]">Receive verification codes via email</p>
              </div>
            </label>

            <label className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="otpDeliveryMethod"
                value="sms"
                checked={otpDeliveryMethod === 'sms'}
                onChange={() => setOtpDeliveryMethod('sms')}
                className="mt-0.5 w-4 h-4 text-[#74AA9C] focus:ring-[#74AA9C] border-gray-300"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-[#080808]">Text Message (SMS)</span>
                <p className="text-xs text-[#6B7280]">Receive verification codes via text message</p>
              </div>
            </label>
          </div>

          <div className="mt-4">
            <button
              onClick={handleSaveOtpPreference}
              disabled={isSavingOtpPreference || otpDeliveryMethod === user.otp_delivery_method}
              className="px-4 py-2 bg-[#74AA9C] text-white text-sm rounded-lg hover:bg-[#5D8A7E] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isSavingOtpPreference ? 'Saving...' : 'Save Preference'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
